import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  getStaffThread,
  getStaffMessages,
  postStaffMessage,
  markStaffRead,
  createChatInvite,
  getStaffAttachmentBlob,
} from "@/lib/chat";
import { connectStaffChatSocket } from "@/lib/chat_ws";

const unwrap = (res) =>
  res && typeof res === "object" && "data" in res ? res.data : res;

const isAbsoluteUrl = (s) => /^https?:\/\//i.test(String(s || ""));

const getThreadId = (t) => t?.id || t?.thread_id || t?.threadId || null;

function getBusinessIdFromStorage() {
  try {
    return window?.localStorage?.getItem("current_business_id") || null;
  } catch {
    return null;
  }
}

function prettyBytes(n) {
  const v = Number(n || 0);
  if (!isFinite(v) || v <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let x = v;
  while (x >= 1024 && i < units.length - 1) {
    x /= 1024;
    i += 1;
  }
  return `${x.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function isImageFile(file) {
  const t = String(file?.type || "").toLowerCase();
  return t.startsWith("image/");
}

function isImageAttachment(att) {
  const ct = String(
    att?.content_type ??
    att?.original_content_type ??
    att?.contentType ??
    att?.mime ??
    ""
  ).toLowerCase();

  return (
    ct.startsWith("image/") ||
    ct.includes("webp") ||
    ct.includes("jpeg") ||
    ct.includes("jpg") ||
    ct.includes("png")
  );
}

function StaffAttachmentThumb({ att, businessId, cacheRef, bump }) {
  const [src, setSrc] = useState("");

  useEffect(() => {
    let alive = true;

    const id = att?.id ?? att?.attachment_id ?? att?.attachmentId;
    if (!id) return;

    const cached = cacheRef.current.get(String(id));
    if (cached?.url) {
      setSrc(cached.url);
      return;
    }

    (async () => {
      try {
        const blob = await getStaffAttachmentBlob(id, { businessId });
        if (!alive) return;

        const url = URL.createObjectURL(blob);
        cacheRef.current.set(String(id), { url });
        setSrc(url);
        bump((x) => x + 1);
      } catch {
        // ignore
      }
    })();

    return () => {
      alive = false;
    };
  }, [att, businessId, cacheRef, bump]);

  if (!src) {
    return <div className="h-24 w-24 rounded-lg border bg-muted/40 animate-pulse" />;
  }

  return (
    <a
      href={src}
      target="_blank"
      rel="noreferrer"
      className="block h-24 w-24 overflow-hidden rounded-lg border hover:opacity-90"
      title="Deschide imaginea"
    >
      <img
        src={src}
        alt={att?.original_name || att?.name || "attachment"}
        className="h-full w-full object-cover"
        loading="lazy"
      />
    </a>
  );
}

export default function AppointmentChatPage() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();

  const [thread, setThread] = useState(null);
  const [apptInfo, setApptInfo] = useState(null);

  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");

  const [pendingFiles, setPendingFiles] = useState([]); // File[]
  const [pendingPreviews, setPendingPreviews] = useState([]); // {file,url}[]
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const [isAtBottom, setIsAtBottom] = useState(true);
  const [newMsgCount, setNewMsgCount] = useState(0);

  const listRef = useRef(null);
  const bottomRef = useRef(null);

  // refs pentru polling fără restart continuu
  const apptIdRef = useRef(appointmentId);
  const threadIdRef = useRef(null);
  const messagesRef = useRef([]);
  const lastCreatedAtRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);

  // cache pentru objectURL attachments (staff)
  const attachmentUrlCacheRef = useRef(new Map());
  const [, forceRerender] = useState(0);

  const effectiveBusinessId = useMemo(() => getBusinessIdFromStorage(), []);

  useEffect(() => {
    apptIdRef.current = appointmentId;
  }, [appointmentId]);

  useEffect(() => {
    messagesRef.current = messages;
    const last = messages[messages.length - 1];
    lastCreatedAtRef.current = last?.created_at || null;
  }, [messages]);

  const scrollToBottom = (behavior = "auto") =>
    bottomRef.current?.scrollIntoView({ behavior });

  const isNearBottom = () => {
    const el = listRef.current;
    if (!el) return true;
    const threshold = 80;
    return el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
  };

  const onScrollMessages = () => {
    const near = isNearBottom();
    shouldAutoScrollRef.current = near;
    setIsAtBottom(near);
    if (near) setNewMsgCount(0);
  };

  const clearPendingFiles = () => {
    try {
      pendingPreviews.forEach((p) => p?.url && URL.revokeObjectURL(p.url));
    } catch {
      // ignore
    }
    setPendingFiles([]);
    setPendingPreviews([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const revokeAllAttachmentUrls = () => {
    try {
      for (const v of attachmentUrlCacheRef.current.values()) {
        if (v?.url) URL.revokeObjectURL(v.url);
      }
    } catch {
      // ignore
    }
    attachmentUrlCacheRef.current.clear();
  };

  useEffect(() => {
    return () => {
      clearPendingFiles();
      revokeAllAttachmentUrls();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePickFiles = (e) => {
    const list = Array.from(e?.target?.files || []);
    if (!list.length) return;

    const MAX_FILES_TOTAL = 5;
    const MAX_BYTES_EACH = 10 * 1024 * 1024; // 10MB

    const incoming = list.filter((f) => isImageFile(f));
    const filtered = incoming.filter((f) => (f?.size || 0) <= MAX_BYTES_EACH);

    const nextFiles = [...pendingFiles, ...filtered].slice(0, MAX_FILES_TOTAL);

    // rebuild previews
    pendingPreviews.forEach((p) => p?.url && URL.revokeObjectURL(p.url));
    const nextPreviews = nextFiles.map((f) => ({ file: f, url: URL.createObjectURL(f) }));

    setPendingFiles(nextFiles);
    setPendingPreviews(nextPreviews);

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removePendingAt = (idx) => {
    const prev = pendingPreviews[idx];
    if (prev?.url) {
      try {
        URL.revokeObjectURL(prev.url);
      } catch {
        // ignore
      }
    }
    const nextFiles = pendingFiles.filter((_, i) => i !== idx);

    pendingPreviews.forEach((p, i) => {
      if (i !== idx && p?.url) {
        try {
          URL.revokeObjectURL(p.url);
        } catch {
          // ignore
        }
      }
    });

    const nextPreviews = nextFiles.map((f) => ({ file: f, url: URL.createObjectURL(f) }));

    setPendingFiles(nextFiles);
    setPendingPreviews(nextPreviews);
  };

  async function ensureThread(apptId) {
    // 1) încearcă direct
    try {
      const t1 = unwrap(await getStaffThread(apptId, { businessId: effectiveBusinessId }));
      const tid1 = getThreadId(t1);
      if (tid1) return t1;
    } catch {
      // ignore
    }

    // 2) încearcă să creeze invite (thread) și apoi reia
    try {
      await createChatInvite(apptId, { sendSms: false }, { businessId: effectiveBusinessId });
    } catch {
      // ok
    }

    const t2 = unwrap(await getStaffThread(apptId, { businessId: effectiveBusinessId }));
    const tid2 = getThreadId(t2);
    if (tid2) return t2;

    return null;
  }

  // LOAD initial (thread + mesaje + appointment)
  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setError("");

      try {
        const apptId = appointmentId;

        const t = await ensureThread(apptId);
        const tid = getThreadId(t);

        if (!mounted) return;

        if (!t || !tid) {
          setThread(null);
          setMessages([]);
          setError("Chat indisponibil pentru această programare.");
          return;
        }

        threadIdRef.current = tid;
        setThread(t || null);

        // mesaje pe threadId
        const mRes = await getStaffMessages(
          tid,
          { limit: 100 },
          { businessId: effectiveBusinessId }
        );
        const m = unwrap(mRes);

        // appointment details (best effort)
        let a = null;
        try {
          const aRes = await api.get(`/b/appointments/${apptId}`);
          a = unwrap(aRes);
        } catch {
          // ignore
        }

        if (!mounted) return;

        setApptInfo(a || null);
        setMessages(Array.isArray(m) ? m : []);
        setIsAtBottom(true);
        setNewMsgCount(0);

        markStaffRead(tid, { businessId: effectiveBusinessId }).catch(() => {});
        requestAnimationFrame(() => scrollToBottom("auto"));
      } catch (e) {
        if (!mounted) return;
        setThread(null);
        setApptInfo(null);
        setMessages([]);
        setError("Chat indisponibil pentru această programare.");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId]);

  
  // REALTIME (WebSocket pe threadId)
  useEffect(() => {
    const tid = threadIdRef.current || thread?.id;
    if (!tid) return;
    if (!effectiveBusinessId) return;

    const sock = connectStaffChatSocket({
      businessId: effectiveBusinessId,
      appointmentOrThreadId: tid,
      onEvent: (ev) => {
        if (!ev || typeof ev !== "object") return;

        if (ev.type === "chat.message") {
          const msg = ev?.message;
          if (!msg || !msg.id) return;

          setMessages((prev) => {
            const arr = Array.isArray(prev) ? prev : [];
            if (arr.some((x) => x?.id === msg.id)) return arr;
            return [...arr, msg];
          });

          try {
            const dt = msg?.created_at || msg?.createdAt;
            if (dt) lastCreatedAtRef.current = dt;
          } catch {}

          // mark read pentru staff (best-effort)
          markStaffRead(tid, { businessId: effectiveBusinessId }).catch(() => {});
        }
      },
    });

    return () => {
      try {
        sock?.close?.();
      } catch {}
    };
  }, [thread?.id, effectiveBusinessId]);
async function onSend(e) {
    e.preventDefault();

    const text = (body || "").trim();
    const files = pendingFiles || [];

    if ((!text && !files.length) || sending) return;

    const tid = threadIdRef.current;
    if (!tid) {
      setError("Chat indisponibil momentan.");
      return;
    }

    setSending(true);
    setError("");

    try {
      // ✅ suport body + attachments (FormData din lib/chat.js)
      const res = await postStaffMessage(
        tid,
        { body: text || "" },
        { businessId: effectiveBusinessId, files }
      );
      const msg = unwrap(res);

      const wasAtBottom = isNearBottom();

      if (msg && typeof msg === "object") {
        setMessages((prev) => [...prev, msg]);
      } else {
        // fallback: refresh dacă backend-ul returnează alt format
        try {
          const mRes = await getStaffMessages(
            tid,
            { limit: 100 },
            { businessId: effectiveBusinessId }
          );
          const m = unwrap(mRes);
          setMessages(Array.isArray(m) ? m : []);
        } catch {
          // ignore
        }
      }

      setBody("");
      clearPendingFiles();
      markStaffRead(tid, { businessId: effectiveBusinessId }).catch(() => {});
      if (wasAtBottom) requestAnimationFrame(() => scrollToBottom("smooth"));
    } catch {
      setError("Nu s-a putut trimite mesajul.");
    } finally {
      setSending(false);
    }
  }

  async function onInvite(sendSms) {
    try {
      const res = await createChatInvite(
        appointmentId,
        { sendSms },
        { businessId: effectiveBusinessId }
      );
      const data = unwrap(res);

      const token = data?.client_token || data?.clientToken || data?.token || "";
      const urlFromApi = data?.client_url || data?.clientUrl || "";

      const url = token ? `${window.location.origin}/chat/${token}` : urlFromApi;

      if (url) {
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(url);
        }
        alert(sendSms ? "Link generat + SMS în coadă." : "Link copiat în clipboard.");
      } else {
        alert("Invite generat, dar nu am primit link.");
      }

      // refresh thread după invite (ca să apară token/url)
      try {
        const t = unwrap(await getStaffThread(appointmentId, { businessId: effectiveBusinessId }));
        const tid = getThreadId(t);
        if (tid) {
          threadIdRef.current = tid;
          setThread(t);
        }
      } catch {
        // ignore
      }
    } catch {
      alert("Nu am putut genera invite-ul.");
    }
  }

  if (loading) return <div className="p-6">Se încarcă...</div>;
  if (error) return <div className="p-6">{error}</div>;
  if (!thread) return <div className="p-6">Chat indisponibil.</div>;

  // header info (thread + fallback apptInfo)
  const appt = apptInfo || thread?.appointment || {};
  const biz = thread?.business || apptInfo?.business || {};

  const bizName = biz?.name || apptInfo?.business_name || "Chat programare";

  const apptStart = appt?.start_at || appt?.start || apptInfo?.start_at || apptInfo?.start;
  const apptLabel = apptStart ? new Date(apptStart).toLocaleString() : "-";

  const clientName =
    thread?.client?.name ||
    apptInfo?.client_name ||
    apptInfo?.clientName ||
    apptInfo?.client?.name ||
    appt?.client_name ||
    appt?.clientName ||
    appt?.client?.name ||
    "-";

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto p-4">
        <div className="rounded-xl border border-border bg-card p-4 mb-4 flex items-start justify-between gap-3">
          <div>
            <div className="text-lg font-semibold">{bizName}</div>
            <div className="text-sm text-muted-foreground">Programare: {apptLabel}</div>
            <div className="text-sm text-muted-foreground">Client: {clientName}</div>
          </div>

          <div className="flex gap-2">
            <button
              className="rounded-xl border border-border px-3 py-2 text-sm"
              type="button"
              onClick={() => navigate(-1)}
            >
              Înapoi
            </button>

            <button
              className="rounded-xl border border-border px-3 py-2 text-sm"
              type="button"
              onClick={() => onInvite(false)}
              title="Generează link și copiază"
            >
              Invite (copy)
            </button>

            <button
              className="rounded-xl border border-border px-3 py-2 text-sm"
              type="button"
              onClick={() => onInvite(true)}
              title="Generează link + pune SMS în coadă"
            >
              Invite + SMS
            </button>
          </div>
        </div>

        <div
          ref={listRef}
          onScroll={onScrollMessages}
          className="rounded-xl border border-border bg-card p-4 h-[62vh] overflow-auto"
        >
          {messages.map((m) => {
            const mine = m.sender_type !== "client"; // staff/clinician
            const attachments = Array.isArray(m.attachments)
              ? m.attachments
              : Array.isArray(m.files)
              ? m.files
              : [];

            return (
              <div key={m.id} className={`mb-3 flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className="max-w-[80%] rounded-2xl border border-border px-3 py-2">
                  <div className="text-xs text-muted-foreground mb-1">
                    {m.sender_name || (mine ? "Staff" : "Client")}
                    {" • "}
                    {m.created_at ? new Date(m.created_at).toLocaleTimeString() : ""}
                  </div>

                  {m.body ? <div className="whitespace-pre-wrap">{m.body}</div> : null}

                  {attachments?.length ? (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {attachments
                        .filter((a) => isImageAttachment(a))
                        .map((att) => {
                          const id = att?.id ?? att?.attachment_id ?? att?.attachmentId;
                          if (!id) return null;
                          return (
                            <StaffAttachmentThumb
                              key={String(id)}
                              att={att}
                              businessId={effectiveBusinessId}
                              cacheRef={attachmentUrlCacheRef}
                              bump={forceRerender}
                            />
                          );
                        })}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {!isAtBottom && (
          <div className="mt-2 flex justify-center">
            <button
              type="button"
              onClick={() => {
                shouldAutoScrollRef.current = true;
                setNewMsgCount(0);
                setIsAtBottom(true);
                scrollToBottom("smooth");
              }}
              className="rounded-full border border-border bg-background px-4 py-2 text-sm shadow"
              title="Sari la ultimele mesaje"
            >
              {newMsgCount > 0 ? `${newMsgCount} mesaj(e) noi • Du-mă jos` : "Du-mă jos"}
            </button>
          </div>
        )}

        {/* Composer */}
        <form onSubmit={onSend} className="mt-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handlePickFiles}
          />

          {pendingPreviews.length ? (
            <div className="mb-2 rounded-xl border border-border bg-card p-3">
              <div className="mb-2 text-xs text-muted-foreground">
                Atașamente: {pendingPreviews.length} (max 5)
                <button
                  type="button"
                  className="ml-2 underline hover:opacity-80"
                  onClick={clearPendingFiles}
                  disabled={sending}
                >
                  curăță
                </button>
              </div>

              <div className="flex flex-wrap gap-2">
                {pendingPreviews.map((p, i) => (
                  <div
                    key={`${p.file?.name || "file"}-${i}`}
                    className="relative h-20 w-20 overflow-hidden rounded-lg border"
                  >
                    <img src={p.url} alt={p.file?.name || "preview"} className="h-full w-full object-cover" />
                    <button
                      type="button"
                      className="absolute right-1 top-1 rounded bg-black/60 px-1.5 py-0.5 text-xs text-white hover:bg-black/80"
                      onClick={() => removePendingAt(i)}
                      title="Elimină"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>

              <div className="mt-2 text-[11px] text-muted-foreground">
                Selectate:{" "}
                {pendingFiles
                  .map((f) => `${f.name} (${prettyBytes(f.size)})`)
                  .slice(0, 3)
                  .join(", ")}
                {pendingFiles.length > 3 ? ` +${pendingFiles.length - 3}` : ""}
              </div>
            </div>
          ) : null}

          <div className="flex gap-2">
            <button
              type="button"
              className="rounded-xl border border-border px-3 py-2"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending}
              title="Adaugă poze"
            >
              📎
            </button>

            <input
              className="flex-1 rounded-xl border border-border bg-background px-3 py-2"
              placeholder="Scrie un mesaj..."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={sending}
            />

            <button
              className="rounded-xl border border-border px-4 py-2"
              disabled={sending || (!(body || "").trim() && !pendingFiles.length)}
              type="submit"
            >
              {sending ? "Trimit..." : "Trimite"}
            </button>
          </div>
        </form>

        {thread?.client_token && (
          <div className="mt-3 text-xs text-muted-foreground">
            Public link (debug):{" "}
            <a
              className="underline"
              href={
                isAbsoluteUrl(thread.client_url)
                  ? thread.client_url
                  : `${window.location.origin}/chat/${thread.client_token}`
              }
              target="_blank"
              rel="noreferrer"
            >
              /chat/{String(thread.client_token)}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
